import { type CSSProperties, Fragment, useEffect, useState } from 'react';
import type { StepChild } from './types';
import {
  classes,
  getInitialStep,
  getSpacerClasses,
  getStepClasses,
  isStepChanged,
} from './utils';

export { default as Step, type StepProps } from './Step';

export type StepsBlockProps<T extends string[]> = {
  className?: string;
  style?: CSSProperties;
  name: string;
  children: StepChild[];
  defaultStep?: T[number];
  bodyClassName?: string;
  bodyStyle?: CSSProperties;
  onChange?: (stepName: T[number]) => void;
};

export default function StepsBlock<T extends string[]>({
  name,
  children,
  defaultStep,
  style = {},
  bodyStyle = {},
  className,
  bodyClassName,
  onChange,
}: StepsBlockProps<T>) {
  const [activeStep, setActiveStep] = useState(
    getInitialStep(children, defaultStep)
  );

  useEffect(() => {
    // Handle programmatically passing down a new step
    if (defaultStep && isStepChanged(children, activeStep, defaultStep)) {
      setActiveStep(defaultStep);
    }
  }, [defaultStep]);

  const handleSelectStep = (stepName: T[number]) => {
    // Set locally & optionally pass up to parent component
    setActiveStep(stepName);
    onChange?.(stepName);
  };

  return (
    <div style={style} className={className}>
      <div className={classes.wrapper}>
        {children.map((step, i) => (
          // eslint-disable-next-line witmetrics/no-multiline-comments
          /*
           Need to explicitly use <Fragment /> to avoid the `Each child in
           a list should have a unique "key" prop` warning.
           */
          <Fragment key={step.props.name}>
            <div
              className={getStepClasses(step, i, children, name, activeStep)}
              onClick={() =>
                !step.props.disabled && handleSelectStep(step.props.name)
              }>
              {step.props.label}
              {step.props.adornment}
            </div>
            {i !== children.length - 1 && (
              <div className={getSpacerClasses(i, children, activeStep)} />
            )}
          </Fragment>
        ))}
      </div>
      <div style={bodyStyle} className={bodyClassName}>
        {children.find(({ props: { name } }) => name === activeStep)}
      </div>
    </div>
  );
}
